import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../services/axiosInterceptor";

/*
UPLOAD + ANALYZE
*/
export const uploadAndAnalyzeThunk = createAsyncThunk(
  "smartMerge/uploadAnalyze",
  async ({ file1, file2, extraFiles = [], isExtraOnly = false, filesToUpload = [], onUploadProgress }, { rejectWithValue, signal }) => {
    try {
      const formData = new FormData();
      
      if (isExtraOnly) {
        filesToUpload.forEach((file) => formData.append("files", file));
      } else {
        formData.append("files", file1);
        formData.append("files", file2);
        extraFiles.forEach((file) => formData.append("files", file));
      }

      const response = await apiClient.post(
        "/file_merging/clean-and-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress,
          signal,
        }
      );

      return { ...response.data, isExtraOnly };

    } catch (error) {
      if (error?.name === 'CanceledError' || error?.message === 'canceled') {
        throw error;
      }
      return rejectWithValue(
        error.response?.data?.message || "File analysis failed"
      );
    }
  }
);


/*
MERGE FILES
*/
export const mergeFilesThunk = createAsyncThunk(
  "smartMerge/mergeFiles",
  async ({ endpoint, payload }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(endpoint, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail?.suggestion ||
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "Merge failed"
      );
    }
  }
);
const smartMergeSlice = createSlice({
  name: "smartMerge",
  initialState: {
    file1: null,
    file2: null,
    extraFiles: [],
    uploadProgress: 0,
    activeTab: "join",
    appendMapping: [],

    fileUrl1: "",
    fileUrl2: "",

    file1Columns: [],
    file2Columns: [],
    commonColumns: [],

    selectedColumns1: [],
    selectedColumns2: [],

    joinKeys: [{ key1: null, key2: null }],
    joinType: "Inner",

    stepConfigs: [], // Array of { joinKeys, joinType, selectedColumns1, selectedColumns2, file1Columns, file2Columns }

    mergedFileUrl: null,
    blobFilePath: null,

    uploading: false,
    merging: false,

    analysisComplete: false,

    smartMergeMaxFileSize: null,

    // Per-slot sheet metadata: keys are 'file1', 'file2', 'extraFile-0', 'extraFile-1', …
    fileSheetNames: {},  // { [slotKey]: string[] }
    selectedSheets: {},  // { [slotKey]: string | null }

    error: null,
  },

  reducers: {
    setFile1: (state, action) => {
      state.file1 = action.payload;
    },

    setFile2: (state, action) => {
      state.file2 = action.payload;
    },

    setExtraFiles: (state, action) => {
      state.extraFiles = action.payload;
    },

    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },

    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    setAppendMapping: (state, action) => {
      state.appendMapping = action.payload;
    },

    setJoinKeys: (state, action) => {
      state.joinKeys = action.payload;
    },

    setJoinType: (state, action) => {
      state.joinType = action.payload;
    },

    setSelectedColumns1: (state, action) => {
      state.selectedColumns1 = action.payload;
    },

    setSelectedColumns2: (state, action) => {
      state.selectedColumns2 = action.payload;
    },

    updateStepConfig: (state, action) => {
      const { stepIndex, key, value } = action.payload;
      if (state.stepConfigs[stepIndex]) {
        state.stepConfigs[stepIndex][key] = value;
        
        // If the configuration changes, invalidate the cached preview results
        if (["joinKeys", "joinType", "selectedColumns1", "selectedColumns2"].includes(key)) {
            state.stepConfigs[stepIndex].resultBlob = null;
            state.stepConfigs[stepIndex].resultColumns = null;
        }
      }
    },

    setMergedFileUrl: (state, action) => {
      state.mergedFileUrl = action.payload;
    },

    setBlobFilePath: (state, action) => {
      state.blobFilePath = action.payload;
    },

    setSmartMergeMaxFileSize: (state, action) => {
      state.smartMergeMaxFileSize = action.payload;
    },

    setFileSheetNames: (state, action) => {
      const { key, names } = action.payload;
      state.fileSheetNames[key] = names;
    },

    setSelectedSheet: (state, action) => {
      const { key, sheet } = action.payload;
      state.selectedSheets[key] = sheet;
    },

    resetAnalysis: (state) => {
      state.fileUrl1 = "";
      state.fileUrl2 = "";
      state.file1Columns = [];
      state.file2Columns = [];
      state.commonColumns = [];
      state.selectedColumns1 = [];
      state.selectedColumns2 = [];
      state.joinKeys = [{ key1: null, key2: null }];
      state.stepConfigs = [];
      state.analysisComplete = false;
      state.mergedFileUrl = null;
      state.blobFilePath = null;
      state.uploadProgress = 0;
      state.activeTab = "join";
      state.appendMapping = [];
      state.smartMergeMaxFileSize = null;
      // NOTE: fileSheetNames and selectedSheets are NOT cleared here.
      // They are per-slot metadata managed independently by setFileSheetNames /
      // setSelectedSheet and cleared only when a specific file is removed.
    },
  },

  extraReducers: (builder) => {
    /*
    Upload + Analyze
    */
    builder
      .addCase(uploadAndAnalyzeThunk.pending, (state) => {
        state.uploading = true;
      })

      .addCase(uploadAndAnalyzeThunk.fulfilled, (state, action) => {
        const { isExtraOnly } = action.payload;
        const files = action.payload.files || [];

        if (isExtraOnly) {
           const lastStep = state.stepConfigs.length > 0 ? state.stepConfigs[state.stepConfigs.length - 1] : null;
           const inheritedFile1Columns = lastStep ? (lastStep.resultColumns || []) : [];

           for (let i = 0; i < files.length; i++) {
               const f2Obj = files[i];
               const f2Cols = f2Obj?.columns_metadata?.map(c => c.name) || [];

               state.stepConfigs.push({
                 joinKeys: [{ key1: null, key2: null }],
                 joinType: "Inner",
                 selectedColumns1: [],
                 selectedColumns2: f2Cols,
                 file1Columns: inheritedFile1Columns,
                 file2Columns: f2Cols,
                 file2BlobPath: f2Obj?.blob_path || "",
                 resultBlob: null,
                 resultColumns: null,
               });
           }
           state.uploading = false;
           return;
        }

        const file1 = action.payload.file1 || files[0];
        const file2 = action.payload.file2 || files[1];
        const common_columns = action.payload.common_columns;

        const cols1 = file1?.columns_metadata?.map((c) => c.name) || [];
        const cols2 = file2?.columns_metadata?.map((c) => c.name) || [];

        state.fileUrl1 = file1?.blob_path || "";
        state.fileUrl2 = file2?.blob_path || "";

        state.file1Columns = cols1;
        state.file2Columns = cols2;
        state.commonColumns = common_columns;

        state.selectedColumns1 = cols1;
        state.selectedColumns2 = cols2;

        state.joinKeys = common_columns?.length
          ? [{ key1: common_columns[0], key2: common_columns[0] }]
          : [{ key1: null, key2: null }];

        const stepConfigs = [];
        const filesCount = files.length > 0 ? files.length : (file1 && file2 ? 2 : 0);

        for (let i = 0; i < Math.max(1, filesCount - 1); i++) {
          const f1Cols = i === 0 ? cols1 : [];
          
          const f2Obj = i === 0 ? file2 : files[i+1];
          const f2Cols = f2Obj?.columns_metadata?.map(c => c.name) || [];

          const existingStep = state.stepConfigs ? state.stepConfigs[i] : null;

          let initialJoinKeys = existingStep?.joinKeys || [{ key1: null, key2: null }];
          if (!existingStep) {
            if (i === 0 && common_columns?.length) {
              initialJoinKeys = [{ key1: common_columns[0], key2: common_columns[0] }];
            }
          }

          stepConfigs.push({
            joinKeys: initialJoinKeys,
            joinType: existingStep?.joinType || "Inner",
            selectedColumns1: existingStep?.selectedColumns1 || f1Cols,
            selectedColumns2: existingStep?.selectedColumns2 || f2Cols,
            file1Columns: f1Cols,
            file2Columns: f2Cols,
            file2BlobPath: f2Obj?.blob_path || "",
            resultBlob: existingStep?.resultBlob || null,
            resultColumns: existingStep?.resultColumns || null,
          });
        }
        
        state.stepConfigs = stepConfigs;

        state.analysisComplete = true;
        state.uploading = false;
      })

      .addCase(uploadAndAnalyzeThunk.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      });

    /*
    Merge Files
    */
    builder
      .addCase(mergeFilesThunk.pending, (state) => {
        state.merging = true;
        state.mergedFileUrl = null;
        state.blobFilePath = null;
      })

      .addCase(mergeFilesThunk.fulfilled, (state, action) => {
        const downloadUrl =
          action.payload?.merged_file?.download_url ||
          action.payload?.appended_file?.download_url ||
          action.payload?.mergedFileUrl;

        const blobPath =
          action.payload?.merged_file?.blob_path ||
          action.payload?.appended_file?.blob_path ||
          action.payload?.blobFilePath;

        state.mergedFileUrl = downloadUrl;
        state.blobFilePath = blobPath;
        state.merging = false;
      })

      .addCase(mergeFilesThunk.rejected, (state, action) => {
        state.merging = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFile1,
  setFile2,
  setExtraFiles,
  setUploadProgress,
  setActiveTab,
  setAppendMapping,
  setJoinKeys,
  setJoinType,
  setSelectedColumns1,
  setSelectedColumns2,
  updateStepConfig,
  resetAnalysis,
  setMergedFileUrl,
  setBlobFilePath,
  setSmartMergeMaxFileSize,
  setFileSheetNames,
  setSelectedSheet,
} = smartMergeSlice.actions;

export default smartMergeSlice.reducer;